import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, TrendingUp, Users, Award, Lightbulb, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutPage = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const values = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Business-First Approach",
      description: "We treat media and brand building as a system, not just creative work. Every decision is backed by strategy."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Growth-Driven",
      description: "Building things that actually grow businesses. Infrastructure that scales, not just aesthetics that fade."
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: "Innovation & Execution",
      description: "Combining technical expertise with creative vision to deliver full-stack solutions that perform."
    }
  ];

  const timeline = [
    {
      year: "2024",
      title: "Foundation",
      description: "JRT Chideme Media founded with a mission to empower small businesses with enterprise-level digital infrastructure."
    },
    {
      year: "2025",
      title: "First Major Client",
      description: "Launched Chef Compliance UK — delivered brand identity, custom website, booking system, compliance docs, and full business plan."
    },
    {
      year: "2026",
      title: "Team Expansion",
      description: "Elizabeth Gadd joined as CFO, bringing financial expertise and operational structure to scale sustainably."
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Header onContactClick={scrollToContact} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-brand-brown/20 to-black"></div>
        <div className="absolute top-20 right-20 w-96 h-96 bg-brand-maroon/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-brand-olive/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-brand-maroon/20 rounded-full border border-brand-maroon/40 mb-6">
              <span className="text-brand-maroon font-bold">ABOUT US</span>
            </div>
            <h1 className="text-6xl lg:text-7xl font-bold text-white mb-6 drop-shadow-[0_0_30px_rgba(255,255,255,0.5)]">
              Building Brands.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon via-brand-olive to-brand-maroon drop-shadow-[0_0_20px_rgba(139,35,50,0.8)]">
                Creating Systems.
              </span>{' '}
              Driving Growth.
            </h1>
            <p className="text-2xl text-white leading-relaxed max-w-3xl mx-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
              We're not just a marketing agency — we're your digital infrastructure partner. Every brand deserves a foundation built to last.
            </p>
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient1)" />
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(139, 35, 50, 0.15)" />
              <stop offset="50%" stopColor="rgba(165, 166, 117, 0.15)" />
              <stop offset="100%" stopColor="rgba(139, 35, 50, 0.15)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Leadership Team Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-brand-brown/10 to-black">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              Meet The{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Leadership Team
              </span>
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Two professionals united by a mission: building businesses that last.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* Julius - Founder */}
            <div className="group">
              <div className="relative bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl overflow-hidden border-2 border-brand-maroon/50 hover:border-brand-maroon transition-all duration-300 hover:scale-105 shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10"></div>
                
                {/* Photo */}
                <div className="relative h-96 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_brand-visibility-uk/artifacts/i37n547t_Snapchat-1186129371.jpg"
                    alt="Julius Chideme - Founder & Director"
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                  />
                </div>

                {/* Content */}
                <div className="relative z-20 p-8">
                  <div className="inline-block px-3 py-1 bg-brand-maroon/30 rounded-full border border-brand-maroon/50 text-brand-maroon text-sm font-bold mb-3">
                    FOUNDER & DIRECTOR
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-2 drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]">
                    Julius (JRT) Chideme
                  </h3>
                  
                  <div className="space-y-4 text-white/90 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
                    <p className="leading-relaxed">
                      With an IT background and hands-on technical experience, Julius built JRT Chideme Media from the ground up as a UK-based media consultancy with one clear mission: <span className="text-brand-maroon font-semibold">build businesses, not just brands</span>.
                    </p>
                    <p className="leading-relaxed">
                      Currently studying <span className="text-brand-olive font-semibold">BSc Computing at Global Banking School, Manchester</span>, Julius combines academic rigor with real-world execution. His business-first mindset treats media and brand building as a system — approaching every project with strategy, infrastructure, and sustainable growth in mind.
                    </p>
                    <p className="leading-relaxed">
                      Under his leadership, JRT Chideme Media delivered its first major client success: <span className="text-brand-maroon font-semibold">Chef Compliance UK</span> — a full-stack solution including brand identity, custom website, booking system, pricing structure, compliance documents, social media setup, hosting, and a comprehensive business plan.
                    </p>
                  </div>

                  <div className="mt-6 pt-6 border-t border-brand-olive/30">
                    <p className="text-brand-cream italic drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
                      "Every brand deserves infrastructure, not just aesthetics. We build things that actually grow businesses."
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Elizabeth - CFO */}
            <div className="group">
              <div className="relative bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl overflow-hidden border-2 border-brand-olive/50 hover:border-brand-olive transition-all duration-300 hover:scale-105 shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-10"></div>
                
                {/* Photo */}
                <div className="relative h-96 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_brand-visibility-uk/artifacts/lfsiy78a_profile.jpg"
                    alt="Elizabeth Gadd - Chief Financial Officer"
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                  />
                </div>

                {/* Content */}
                <div className="relative z-20 p-8">
                  <div className="inline-block px-3 py-1 bg-brand-olive/30 rounded-full border border-brand-olive/50 text-brand-olive text-sm font-bold mb-3">
                    CHIEF FINANCIAL OFFICER
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-2 drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]">
                    Elizabeth Gadd
                  </h3>
                  
                  <div className="space-y-4 text-white/90 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
                    <p className="leading-relaxed">
                      As CFO and second-in-command, Elizabeth brings the financial expertise and operational structure that transforms vision into sustainable reality. Her role goes beyond numbers — she's the architect of JRT Chideme Media's <span className="text-brand-olive font-semibold">financial backbone</span>.
                    </p>
                    <p className="leading-relaxed">
                      Elizabeth oversees the financial health of the business, ensuring every client project is managed sustainably and delivers measurable ROI. Her strategic approach to budgeting, resource allocation, and growth planning enables the agency to scale without compromising quality or client results.
                    </p>
                    <p className="leading-relaxed">
                      With Elizabeth at the helm of operations, JRT Chideme Media has the infrastructure to take on ambitious projects and deliver <span className="text-brand-olive font-semibold">real, data-driven results</span> for clients.
                    </p>
                  </div>

                  <div className="mt-6 pt-6 border-t border-brand-maroon/30">
                    <p className="text-brand-cream italic drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
                      "Every great brand needs infrastructure behind it. I'm here to make sure JRT Chideme Media has the financial backbone to grow sustainably and deliver real results for our clients."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient2)" />
          <defs>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(139, 35, 50, 0.15)" />
              <stop offset="50%" stopColor="rgba(165, 166, 117, 0.15)" />
              <stop offset="100%" stopColor="rgba(139, 35, 50, 0.15)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Core Values Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-brand-brown/10">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              Our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Core Values
              </span>
            </h2>
            <p className="text-xl text-white/80">
              The principles that guide every project, every client relationship, every decision.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-brand-brown/40 to-black/60 backdrop-blur-xl rounded-2xl p-8 border border-brand-olive/30 hover:border-brand-maroon/50 transition-all duration-300 hover:scale-105 shadow-lg"
              >
                <div className="w-16 h-16 bg-brand-maroon/20 rounded-xl flex items-center justify-center mb-6 text-brand-maroon">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">
                  {value.title}
                </h3>
                <p className="text-white/80 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-brand-brown/10 to-black">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              Our{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Journey
              </span>
            </h2>
            <p className="text-xl text-white/80">
              From foundation to industry leader — every milestone tells a story.
            </p>
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-brand-maroon via-brand-olive to-brand-maroon"></div>

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className="relative pl-20">
                  {/* Dot */}
                  <div className="absolute left-0 w-16 h-16 bg-gradient-to-br from-brand-maroon to-brand-olive rounded-full flex items-center justify-center border-4 border-black shadow-lg">
                    <span className="text-white font-bold text-sm">{item.year}</span>
                  </div>

                  {/* Content */}
                  <div className="bg-gradient-to-br from-brand-brown/40 to-black/60 backdrop-blur-xl rounded-2xl p-8 border border-brand-olive/30 hover:border-brand-maroon/50 transition-all duration-300 shadow-lg">
                    <h3 className="text-2xl font-bold text-white mb-3 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">
                      {item.title}
                    </h3>
                    <p className="text-white/80 leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-brand-brown/10">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="bg-gradient-to-br from-brand-maroon/20 to-brand-olive/20 backdrop-blur-xl rounded-3xl p-12 border-2 border-brand-maroon/50 shadow-2xl">
            <Award className="w-16 h-16 text-brand-maroon mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-6 drop-shadow-[0_0_20px_rgba(255,255,255,0.4)]">
              Ready to Build Something{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-maroon to-brand-olive">
                Extraordinary?
              </span>
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              Let's discuss how we can build the infrastructure your brand needs to thrive. Book a free consultation and see what's possible.
            </p>
            <Button
              onClick={scrollToContact}
              className="bg-brand-maroon hover:bg-brand-maroon/90 text-white font-bold text-lg px-12 py-6 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg shadow-brand-maroon/50"
            >
              Book Your Free Consultation
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
